<template>
  <div>
    <div v-if="!loading">
      <div v-if="selectTeam">
        <div v-if="teams.length">
          <b-alert
            v-if="isTransferMode"
            variant="info"
            show
            class="p-1"
          >
            {{ $t('order.team.transfer-description') }}
          </b-alert>
          <div
            v-if="showSupportingTeamSelectionLabel"
            class="font-small-3 text-uppercase text-secondary mt-1"
          >
            {{ $t('order.team.add-supporting-team-label') }}
          </div>
          <label>
            {{ $t('order.team.select') }}
          </label>
          <v-select
            id="select-team"
            v-model="team"
            :clearable="false"
            :searchable="false"
            :options="teams"
            :get-option-label="getOptionLabel"
            :disabled="loading"
            @input="handleTeamSelected"
          >
            <template #option="teamOption">
              <span v-if="teamOption">
                <div class="d-flex flex-column">
                  <span class="font-weight-bold">
                    {{ teamOption.teamDefinition.code }}
                    <span v-if="teamOption.vehicle && teamOption.vehicle.sideNumber">
                      · {{ teamOption.vehicle.sideNumber }}
                    </span>
                  </span>
                  <small class="text-muted">
                    {{ describeTeamMembers(teamOption) }}
                  </small>
                  <small class="text-muted">
                    {{ describeTeamTypes(teamOption) }}
                  </small>
                </div>
              </span>
            </template>
            <template #no-options>
              <span class="text-center">
                {{ loading ? $t('shared.loading') : $t('order.team.no-teams-warning') }}
              </span>
            </template>
          </v-select>
          <div
            v-if="requiresReasonConfirmation"
            class="mt-1"
          >
            <label for="team-transfer-reason">
              {{ reasonLabel }}
            </label>
            <b-form-textarea
              id="team-transfer-reason"
              v-model.trim="transferReason"
              rows="3"
              max-rows="5"
            />
            <b-form-checkbox
              v-model="transferConfirmed"
              class="mt-50"
            >
              {{ confirmationLabel }}
            </b-form-checkbox>
          </div>

        </div>
        <div v-else-if="!loading">
          <b-alert
            variant="danger"
            class="p-1"
            :show.sync="!teams.length"
          >
            {{ $t('order.team.no-teams-warning') }}
          </b-alert>
        </div>
      </div>
      <div
        v-if="showReleaseForm"
        class="mt-1"
      >
        <label for="team-release-reason">
          {{ reasonLabel }}
        </label>
        <b-form-textarea
          id="team-release-reason"
          v-model.trim="transferReason"
          rows="3"
          max-rows="5"
        />
        <b-form-checkbox
          v-model="transferConfirmed"
          class="mt-50"
        >
          {{ confirmationLabel }}
        </b-form-checkbox>
      </div>
      <div
        v-if="hasAtLeastOneRole(me.roles, ['ROLE_DISPATCHER'])"
        class="text-right mt-1"
      >
        <template v-if="showActionButtons">
          <b-button
            v-if="canAddSupportingTeam"
            variant="outline-primary"
            :disabled="saving"
            class="ml-50"
            @click="startAssignSupportingTeam"
          >
            {{ $t('order.team.add-team') }}
          </b-button>
          <b-button
            v-if="hasAssignedTeam && canEditTeam"
            variant="outline-warning"
            :disabled="saving"
            class="ml-50"
            @click="changeTeam"
          >
            {{ $t('order.team.change-team') }}
          </b-button>
          <b-button
            v-if="canReleaseTeam"
            variant="outline-danger"
            :disabled="saving"
            class="ml-50"
            @click="startRelease"
          >
            {{ releaseLabel }}
          </b-button>
        </template>
        <b-button
          v-show="teamCopy.id || showReleaseForm"
          variant="outline-secondary"
          :disabled="saving"
          class="ml-50"
          @click="cancelChanging"
        >
          {{ $t('shared.button.cancel') }}
        </b-button>
        <b-button
          v-show="showSubmitButton"
          class="ml-50"
          variant="primary"
          :disabled="isSubmitDisabled"
          @click="submit(shouldSelectTeamAfterSubmit)"
        >{{
          $t('shared.button.submit')
        }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import { cloneNested, parseRequestError } from '@/helpers/helpers'
import guard from '@/guard'
import PusherClient from '@/libs/pusher'

export default {
  components: {
    vSelect,
  },
  props: {
    order: {
      type: Object,
      default: null,
    },
    workflow: {
      type: Object,
      default: null,
    },
    releaseTeamId: {
      type: Number,
      default: null,
    },
    showActionButtons: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      team: {},
      teamCopy: {},
      selectTeam: false,
      teamUpdateHandler: null,
      pendingTeamRefresh: false,
      transferReason: '',
      transferConfirmed: false,
      actionMode: null,
    }
  },
  computed: {
    isBusy() {
      return this.loading || this.saving
    },
    teams() {
      const teams = this.$store.getters['team/getTeams'] || []
      const currentOrderId = this.order?.id || null

      return teams.filter(team => {
        const teamCurrentOrderId = team?.currentOrder?.id || null

        return !teamCurrentOrderId || teamCurrentOrderId === currentOrderId
      })
    },
    me() {
      return this.$store.getters['auth/getUser']
    },
    selectedTeamDefinition() {
      if (this.team && this.team.teamDefinition) {
        return this.team.teamDefinition
      }
      if (this.order && this.order.team && this.order.team.teamDefinition) {
        return this.order.team.teamDefinition
      }
      return null
    },
    canManageTeams() {
      return guard.hasAtLeastOneRole(this.me.roles || [], ['ROLE_DISPATCHER'])
    },
    assignAction() {
      return this.workflow?.teamActions?.assign || null
    },
    transferAction() {
      return this.workflow?.teamActions?.transfer || null
    },
    releaseAction() {
      return this.workflow?.teamActions?.release || null
    },
    selectedAction() {
      const actions = {
        assign: this.assignAction,
        transfer: this.transferAction,
        release: this.releaseAction,
      }

      return actions[this.actionMode] || null
    },
    isTransferMode() {
      return this.actionMode === 'transfer'
    },
    showReleaseForm() {
      return this.actionMode === 'release' && this.requiresReasonConfirmation
    },
    requiresReasonConfirmation() {
      return this.hasRequiredInput('reason') || this.hasRequiredInput('confirmation')
    },
    reasonLabel() {
      return this.$t('order.team.transfer-reason')
    },
    confirmationLabel() {
      return this.$t('order.team.transfer-confirmed')
    },
    canAssignTeam() {
      return !!this.assignAction?.allowed
    },
    canTransferTeam() {
      return !!this.transferAction?.allowed
    },
    canReleaseTeam() {
      return !!this.releaseAction?.allowed
    },
    canEditTeam() {
      return this.canAssignTeam || this.canTransferTeam
    },
    canAddSupportingTeam() {
      return this.canManageTeams
        && this.hasAssignedTeam
        && this.canAssignTeam
        && !this.selectTeam
        && this.actionMode !== 'release'
    },
    showSupportingTeamSelectionLabel() {
      return this.selectTeam && this.actionMode === 'assign' && this.hasAssignedTeam
    },
    hasAssignedTeam() {
      return !!this.order?.team?.id
    },
    isSubmitDisabled() {
      if (!this.requestVersion || !this.actionMode) {
        return true
      }

      if (this.saving) {
        return true
      }

      if (this.actionMode !== 'release' && (!this.team || !this.team.id)) {
        return true
      }

      if (!this.requiresReasonConfirmation) {
        return false
      }

      return !this.transferConfirmed || !this.transferReason
    },
    showSubmitButton() {
      return this.showReleaseForm || (this.selectTeam && this.requiresReasonConfirmation)
    },
    shouldSelectTeamAfterSubmit() {
      return this.actionMode === 'release'
        && this.canAssignTeam
        && !this.hasAlternativeActiveTeamAssignments
    },
    activeTeamAssignments() {
      return Array.isArray(this.order?.teamAssignments)
        ? this.order.teamAssignments.filter(assignment => assignment && !assignment.releasedAt)
        : []
    },
    hasAlternativeActiveTeamAssignments() {
      if (!this.hasAssignedTeam) {
        return false
      }

      const releasedTeamId = this.releaseTeamId || this.order?.team?.id || null

      return this.activeTeamAssignments.some(assignment => assignment?.team?.id && assignment.team.id !== releasedTeamId)
    },
    releaseLabel() {
      const labelKey = this.releaseAction?.labelKey

      if (labelKey && typeof this.$te === 'function' && this.$te(labelKey)) {
        return this.$t(labelKey)
      }

      return this.$t('order.team.unbind-team')
    },
    requestVersion() {
      const orderVersion = Number(this.order?.version)

      return Number.isFinite(orderVersion) ? orderVersion : null
    },
  },
  watch: {
    isBusy: {
      immediate: true,
      handler(value) {
        this.$emit('busy-change', value)
      },
    },
  },
  mounted() {
    if (!this.canManageTeams) {
      return
    }
    this.bindTeamUpdates()
    this.init()
    if (!this.hasAssignedTeam && this.canAssignTeam) {
      this.selectTeam = true
      this.actionMode = 'assign'
    }
  },
  beforeDestroy() {
    this.unbindTeamUpdates()
  },
  methods: {
    hasAtLeastOneRole: guard.hasAtLeastOneRole,
    bindTeamUpdates() {
      if (!this.canManageTeams) {
        return
      }
      if (this.teamUpdateHandler) {
        return
      }
      this.teamUpdateHandler = () => {
        this.handleTeamUpdate()
      }
      PusherClient.bind('update', 'team', this.teamUpdateHandler, this)
    },
    unbindTeamUpdates() {
      if (!this.teamUpdateHandler) {
        return
      }
      PusherClient.unbind('update', 'team', this.teamUpdateHandler, this)
      this.teamUpdateHandler = null
    },
    handleTeamUpdate() {
      if (!this.canManageTeams) {
        return
      }

      if (this.saving) {
        this.pendingTeamRefresh = true
        return
      }

      this.init(true)
    },
    init(force = false) {
      if (!this.canManageTeams) {
        return
      }
      if (this.loading && !force) {
        return
      }
      this.loading = true
      const query = {}
      if (this.order && this.order.id) {
        query.orderId = this.order.id
      }

      this.$store.dispatch('team/fetchTeamsForAssign', query)
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    getOptionLabel(team) {
      if (!team || !team.teamDefinition || !team.teamDefinition.code) {
        return ''
      }

      return team.teamDefinition.code
    },
    submit(selectTeamOnEnd = false) {
      if (!this.canManageTeams) {
        return
      }
      this.saving = true
      const requestPayload = {
        id: this.order.id,
        action: this.actionMode,
        data: this.buildPayload(),
      }
      this.$store.dispatch('order/updateOrderTeam', requestPayload)
        .then(() => {
          this.teamCopy = {}
          window.toast.notify.success(this.$t('order.notify.edit.success_title'))
          this.selectTeam = selectTeamOnEnd
          return this.$store.dispatch('order/fetchOrderById', this.order.id)
        })
        .catch(err => {
          parseRequestError(err)
        })
        .finally(() => {
          this.loading = false
          this.teamCopy = {}
          this.saving = false
          if (this.pendingTeamRefresh || this.selectTeam) {
            this.pendingTeamRefresh = false
            this.init(true)
          }
          this.resetTransferState()
        })
    },
    handleTeamSelected(selectedTeam) {
      if (!this.canManageTeams || this.saving) {
        return
      }

      if (!selectedTeam || !selectedTeam.id) {
        return
      }

      if (this.actionMode === 'release') {
        this.switchReleaseToTeamSelectionMode()
      }

      if (this.isTransferMode) {
        if (!this.requiresReasonConfirmation) {
          this.submit(false)
        }

        return
      }

      this.submit(false)
    },
    changeTeam() {
      if (!this.canManageTeams) {
        return
      }
      this.init()
      this.selectTeam = true
      this.actionMode = this.resolveTeamSelectionMode()
      this.teamCopy = cloneNested(this.order.team)
      this.team = {}
      this.resetTransferState()
      this.$store.commit('order/SET_ORDER_TEAM', {})
    },
    startAssignSupportingTeam() {
      if (!this.canManageTeams || !this.canAssignTeam) {
        return
      }

      this.init()
      this.selectTeam = true
      this.actionMode = 'assign'
      this.teamCopy = cloneNested(this.order.team)
      this.team = {}
      this.resetTransferState()
    },
    cancelChanging() {
      if (!this.canManageTeams) {
        return
      }
      this.selectTeam = false
      this.actionMode = null
      this.$store.commit('order/SET_ORDER_TEAM', this.teamCopy)
      this.teamCopy = {}
      this.resetTransferState()
    },
    startRelease() {
      if (!this.canManageTeams || !this.canReleaseTeam) {
        return
      }

      this.selectTeam = false
      this.actionMode = 'release'
      this.teamCopy = cloneNested(this.order.team)
      this.resetTransferState()

      if (!this.requiresReasonConfirmation) {
        this.submit(this.shouldSelectTeamAfterSubmit)
      }
    },
    resetTransferState() {
      this.transferReason = ''
      this.transferConfirmed = false
    },
    hasRequiredInput(inputName) {
      return Array.isArray(this.selectedAction?.requiredInputs)
        && this.selectedAction.requiredInputs.some(input => input?.name === inputName)
    },
    resolveTeamSelectionMode() {
      return this.canTransferTeam ? 'transfer' : 'assign'
    },
    switchReleaseToTeamSelectionMode() {
      this.actionMode = this.resolveTeamSelectionMode()

      if (window && window.toast && window.toast.notify && window.toast.notify.info) {
        window.toast.notify.info(this.$t('order.team.release-switched-to-assign'))
      }
    },
    buildPayload() {
      const basePayload = {
        version: this.requestVersion,
      }

      if (this.actionMode === 'release') {
        return {
          ...basePayload,
          teamId: this.releaseTeamId || this.order?.team?.id || null,
          confirmation: this.transferConfirmed,
          reason: this.transferReason || null,
        }
      }

      return {
        ...basePayload,
        teamId: this.team.id ? this.team.id : null,
        confirmation: this.requiresReasonConfirmation ? this.transferConfirmed : false,
        reason: this.requiresReasonConfirmation ? this.transferReason : null,
      }
    },
    formatUserSummary(user) {
      if (!user) {
        return this.$t('shared.no-data')
      }
      const surname = user.surname || ''
      const name = user.name || ''
      const summary = `${surname} ${name}`.trim()
      return summary || this.$t('shared.no-data')
    },
    describeTeamMembers(team) {
      if (!team) {
        return ''
      }
      const driverLabel = `${this.$t('dashboard.teams.label.driver-user')}: ${this.formatUserSummary(team.driverUser)}`
      if (team.teamDefinition?.driverOnly || !team.paramedicUser) {
        return `${driverLabel} (${this.$t('team.driver-only')})`
      }
      const paramedicLabel = `${this.$t('dashboard.teams.label.paramedic-user')}: ${this.formatUserSummary(team.paramedicUser)}`
      return `${paramedicLabel} | ${driverLabel}`
    },
    describeTeamTypes(team) {
      const types = Array.isArray(team?.teamDefinition?.types) ? team.teamDefinition.types.filter(Boolean) : []
      if (!types.length) {
        return this.$t('team.view.no-order-types')
      }
      const translated = types.map(type => this.$t(`order.types.${type}`))
      return `${this.$t('order.team.supported-types')}: ${translated.join(', ')}`
    },
  },
}
</script>
